import { useGtmDataLayer } from 'src/hooks/useGtmDataLayer';
import {
  ClickAnalyticsType,
  ContactInteractionAnalytics,
  NestedObject,
} from 'src/types';
import { detectExternalLink } from 'src/utils/detectExternalLink';
import { sendYMData } from 'src/utils/sendYMData';

import { metricClient } from '../utils/metricClient';

interface FillFormFieldPopup {
  formType: 'popup';
  popupPlace: 'header' | 'body' | 'footer';
  buttonId: string;
}

export type AnalyticsFormType =
  | 'sale'
  | 'consultation'
  | 'consultation-lk'
  | 'calculator'
  | 'promo'
  | 'feedback'
  | 'webinar'
  | 'hr'
  | 'partners'
  | 'marketplace'
  | 'subscription'
  | 'education'
  | 'other';

export interface TryFormProps {
  formType: AnalyticsFormType;
  formName: FormNameAnalytics;
  isPopup?: boolean;
  action: 'Отправлено' | 'Ошибка' | 'Поля';
  errorCode?: string;
  fields?: string[];
}

export interface FormInteraction {
  formName: FormNameAnalytics;
  isPopup?: boolean;
}

export interface FormView {
  formName: FormNameAnalytics;
  typeView: 'scroll' | 'anchor' | 'pop-up';
  isPopup?: boolean;
}

export type searchAnalyticsType =
  | 'header'
  | 'serp'
  | 'live-menu-products'
  | 'live-page-products';

type SearchClicksAnalyticsType = {
  link: string;
  searchPhrase: string;
  type: 'menu-products' | 'page-all-products' | 'Search-SERP';
  source?: SearchClickAnalyticsSource;
};

type SearchResultClicksAnalytics = {
  link: string;
  searchPhrase: string;
  type: searchAnalyticsType;
  source?: SearchClickAnalyticsSource;
  category: 'site-body' | 'site-header' | 'site-footer';
  requestId: string;
  positionNumber?: string;
};

export type SearchClickAnalyticsSource = 'all' | 'filter' | 'e-search';
interface FillFormFieldBody {
  formType: 'body';
  popupPlace?: undefined;
  buttonId?: undefined;
}

export type FillFormFieldBase = { fieldName: string } & (
  | FillFormFieldBody
  | FillFormFieldPopup
);

export enum FormNames {
  platfroms = 'lead-platforms',
  products = 'lead-products',
  solutions = 'lead-solutions',
  services = 'lead-services',
  analogi = 'lead-analogi',
  education = 'lead-education',
  events = 'lead-events',
  offers = 'lead-offers',
  search = 'lead-search',
  subscription = 'lead-subscription',
  subscriptionManagement = 'lead-subscription-management',
  contactsComplianceFormAnonymous = 'lead-contacts-compliance-form-anonymous',
  contactsComplianceFormWithName = 'lead-contacts-compliance-form-with-name',
  registration = 'webinar-registration',
  webinarMaterials = 'webinar-materials',
  digest = 'digest',
  digestBlog = 'digest-blog',
  digestNews = 'digest-news',
  searchRepost = 'search-report',
  consultation = 'consultation',
  withdrawal = 'other-withdrawal',
  additionalMaterials = 'other-additional-materials',
  feedback = 'other-feedback',
  resume = 'other-resume',
  china = 'other-china',
  wr = 'other-wr',
  cl = 'other-cl',
  cmsb = 'other-cmsb',
  unsubscribe = 'other-unsubscribe',
  mkp = 'lead-mkp',
  calculator = 'calculator',
  research = 'research',
  default = 'default',
  registry = 'other-registry',
}

export type FormNameAnalytics = FormNames | `${FormNames}` | AnalyticsFormType;

export type SuccessfullSubmissionFormAnalyticsType = {
  // TODO: когда будем выпиливать старые метрики переделать на 'site-body' | 'site-pop-up'
  zoneclick?: 'B' | 'P'; // body / popup
  formname: FormNameAnalytics;
};

// TODO: понять правильно ли написана функция, удалить и заменить на checkSearchParamsFromUrl
export const checkGetParamsFromUrl = (url: string) => {
  if (
    url.indexOf('autotest') !== -1 ||
    url.indexOf('shadow') !== -1 ||
    url.indexOf('cloudtest') !== -1
  ) {
    return url;
  }

  return url.split('?')[0];
};

export const checkSearchParamsFromUrl = (url: string) => {
  if (
    url.indexOf('autotest') !== -1 ||
    url.indexOf('shadow') !== -1 ||
    url.indexOf('cloudtest') !== -1
  ) {
    return url;
  }

  return url.split('?')[1] ?? 'placeholder';
};

const param5StringResult = (pathname: string) => {
  const pathMap: { [key: string]: string } = {
    products: 'catalog',
    solutions: 'catalog',
    evolution: 'catalog',
    vmware: 'catalog',
    services: 'SEO-pages',
    analogi: 'SEO-pages',
    news: 'blog',
    gocloud: 'gocloud',
    offers: 'offers',
    career: 'career',
    events: 'events',
    cases: 'cases',
    education: 'education',
    search: 'search',
  };

  const resultString = pathname.slice(1).split('/')[0];

  if (!resultString) {
    return 'other';
  }

  return pathMap[resultString] ?? resultString;
};

const checkSpace = () => {
  const url = window.location.href;
  const condition =
    url.indexOf('autotest') !== -1 ||
    url.indexOf('shadow') !== -1 ||
    url.indexOf('cloudtest') !== -1;

  if (
    condition ||
    process.env.NEXT_PUBLIC_SPACE === 'develop' ||
    process.env.NEXT_PUBLIC_SPACE === 'stage'
  ) {
    return 'dev';
  }

  return 'prod';
};

export const useAnalytics = () => {
  const { sendDataToDataLayer } = useGtmDataLayer();

  const sendAnalytics = (
    dataLayer: { [key: string]: string } | null,
    params: NestedObject<string>,
    reachGoal: string,
  ) => {
    if (dataLayer) {
      const dataLayerToSend = {
        ...dataLayer,
      };

      if (checkSpace() !== 'dev') {
        sendDataToDataLayer({
          dataLayer: dataLayerToSend,
        });
      }
    }
    sendYMData(reachGoal, params, checkSpace() === 'dev');
  };

  // Form-field-fill
  const fillFormField = ({
    formType,
    fieldName,
    popupPlace,
    buttonId,
  }: FillFormFieldBase) => {
    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'formFill',
      event_label: 'Form-fields-fill',
      retain_url: checkGetParamsFromUrl(window.location.href),
      form_type: formType,
      field_name: fieldName,
    };

    let ymData: NestedObject<string> = {
      'Form-fields-fill': {
        [checkGetParamsFromUrl(window.location.href)]: {
          body: fieldName,
        },
      },
    };

    if (formType === 'popup') {
      dataLayerToSend.popup_place = popupPlace;
      dataLayerToSend.popup_button_id = buttonId;

      ymData = {
        'Form-fields-fill': {
          [checkGetParamsFromUrl(window.location.href)]: {
            popup: {
              [popupPlace]: {
                [fieldName]: buttonId,
              },
            },
          },
        },
      };
    }

    sendAnalytics(dataLayerToSend, ymData, 'Form-fields-fill');

    metricClient.sendEvent(
      {
        category: popupPlace ? 'site-pop-up' : 'site-body',
        entity: formType,
        action: 'Form-field-fill',
      },
      {
        // начинается с 4 параметра
        param1: fieldName,
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: 'site',
        param5: param5StringResult(window.location.pathname),
        param6: popupPlace ? 'pop-up' : 'body',
      },
    );
  };

  const serpClickAnalytics = (
    clickZone: 'site' | 'documentation',
    query: string,
    url: string,
  ) => {
    const analyticsType = 'Serp';

    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_action: 'click',
      event_label: analyticsType,
      retain_url: checkGetParamsFromUrl(window.location.href),
      click_zone: clickZone,
      serp_query: query,
      serp_url: url,
    };

    const ymData = {
      [analyticsType]: {
        [clickZone]: {
          [query]: url,
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, analyticsType);
  };

  const searchAnalytics = (
    type: searchAnalyticsType,
    value: string,
    query: string = '',
  ) => {
    const analyticsType = 'Search';

    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_action: analyticsType,
      event_label: analyticsType,
      retain_url: checkGetParamsFromUrl(window.location.href),
      search_value: value,
      search_type: type,
      search_query: query,
    };

    const ymData = {
      [analyticsType]: {
        [checkGetParamsFromUrl(window.location.href)]: {
          [value]: {
            [type]: query,
          },
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, analyticsType);
  };

  const searchAnalyticsWithRequestId = (
    type: searchAnalyticsType,
    value: string,
    category: 'site-body' | 'site-header' | 'site-footer',
    requestId: string = 'placeholder',
  ) => {
    metricClient.sendEvent(
      {
        category: category,
        entity: type,
        action: 'Search',
      },
      {
        // начинается с 4 параметра
        param1: 'placeholder',
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: value,
        param5: requestId,
      },
    );
  };

  const clickAnalytics = ({
    action,
    clickZone,
    clickElement,
    clickContent,
    uniqueId,
    transitionType,
    clickZoneCategory,
    transitionUrl,
    transitionTypeMetrics,
  }: ClickAnalyticsType) => {
    if (action.startsWith('/')) {
      action = `${window.location.origin}${action}`;
    }
    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'clicks',
      event_label: 'All-clicks',
      retain_url: checkGetParamsFromUrl(window.location.href),
      click_action: action,
      click_zone: clickZone,
      click_element: clickElement,
      click_content: clickContent,
      unique_id: uniqueId,
      transition_type: transitionType,
    };

    const ymData = {
      'All-clicks': {
        [checkGetParamsFromUrl(window.location.href)]: {
          [action]: {
            [clickZone]: {
              [clickElement]: {
                [clickContent]: {
                  [uniqueId]: transitionType,
                },
              },
            },
          },
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, 'All-clicks');

    const transitionUrlForParam7 = transitionUrl ?? 'no-link';

    // новый формат, выше в будущем уйдет
    metricClient.sendEvent(
      {
        category: clickZoneCategory,
        entity: uniqueId,
        action: 'Clicks',
      },
      {
        // начинается с 4 параметра
        param1: clickContent,
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: 'site',
        param5: param5StringResult(window.location.pathname),
        param6: transitionTypeMetrics ?? 'no-link',
        param7: transitionUrlForParam7.startsWith('/')
          ? window.location.origin + transitionUrlForParam7
          : transitionUrlForParam7,
      },
    );
  };

  const pageViewCustom = () => {
    const ymData = {
      'Page-V-custom': {
        [checkGetParamsFromUrl(window.location.href)]: window.location.href,
      },
    };

    sendAnalytics(null, ymData, 'Page-V-custom');

    metricClient.sendEvent(
      {
        category: 'site',
        entity: param5StringResult(window.location.pathname),
        action: 'Page-view-custom',
      },
      {
        // начинается с 4 параметра
        param1: document.referrer || 'direct',
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
      },
    );
  };

  // Form status
  const tryFormAnalytics = ({
    formType,
    formName,
    action,
    errorCode,
    fields,
    isPopup = false,
  }: TryFormProps) => {
    const fieldsString = fields?.join(', ');

    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'tryForm',
      event_label: 'Try-form',
      retain_url: checkGetParamsFromUrl(window.location.href),
      form_type: formType,
      action: action,
    };

    if (action === 'Ошибка') {
      dataLayerToSend.error_code = errorCode!;
    }

    if (action === 'Поля') {
      dataLayerToSend.fields = fieldsString!;
    }

    const ymActionData =
      action === 'Ошибка'
        ? errorCode!
        : action === 'Поля'
        ? fieldsString!
        : 'Успешно отправлено';

    const ymData: NestedObject<string> = {
      'Try-form': {
        [checkGetParamsFromUrl(window.location.href)]: {
          [formType]: {
            [action]: ymActionData,
          },
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, 'Try-form');
    if (action === 'Ошибка' || action === 'Отправлено') {
      // ниже новый формат, то что выше в будущем уйдет
      metricClient.sendEvent(
        {
          category: isPopup ? 'site-pop-up' : 'site-body',
          entity: formName,
          action: 'Form-status',
        },
        {
          // начинается с 4 параметра
          param1: errorCode ?? '200',
          param2: window.location.origin + window.location.pathname,
          param3: checkSearchParamsFromUrl(window.location.href),
          param4: 'site',
          param5: param5StringResult(window.location.pathname),
          param6: isPopup ? 'pop-up' : 'body',
        },
      );
    }
  };

  // Form-interaction, но работает 1 раз, перепроверить
  const funnelFormInput = () => {
    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'funnelFormInput',
      event_label: 'Funnel-form-input',
      retain_url: checkGetParamsFromUrl(window.location.href),
    };

    const inputYmData = {
      'Funnel-form-input': checkGetParamsFromUrl(window.location.href),
    };

    sendAnalytics(dataLayerToSend, inputYmData, 'Funnel-form-input');
  };

  const formInteractionSent = ({
    formName,
    isPopup = false,
  }: FormInteraction) => {
    metricClient.sendEvent(
      {
        category: isPopup ? 'site-pop-up' : 'site-body',
        entity: formName,
        action: 'Form-interaction',
      },
      {
        // начинается с 4 параметра
        param1: 'placeholder',
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: 'site',
        param5: param5StringResult(window.location.pathname),
        param6: isPopup ? 'pop-up' : 'body',
      },
    );
  };

  const formViewSent = ({ formName, typeView }: FormView) => {
    metricClient.sendEvent(
      {
        category: typeView === 'pop-up' ? 'site-pop-up' : 'site-body',
        entity: formName,
        action: 'Form-View',
      },
      {
        // начинается с 4 параметра
        param1: typeView,
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: 'site',
        param5: param5StringResult(window.location.pathname),
      },
    );
  };

  // Form-submit, удалить, когда переведем на новый формат
  const allFormSent = (
    formType: AnalyticsFormType,
    formName: FormNameAnalytics,
    isAdAgree: boolean,
    isPopup = false,
  ) => {
    const agreement = isAdAgree ? 'agree-ad' : 'disagree-ad';
    const formSentFrom = isPopup ? 'popup' : 'body';
    const formsDataLayer = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'formSuccess',
      event_label: 'All-form-sent',
      retain_url: checkGetParamsFromUrl(window.location.href),
      form_type: 'consultation',
      adAgree: agreement,
    };
    const formsYmData = {
      'All-form-sent': {
        [checkGetParamsFromUrl(window.location.href)]: {
          [formType]: {
            [agreement]: formSentFrom,
          },
        },
      },
    };

    sendAnalytics(formsDataLayer, formsYmData, 'All-form-sent');

    // новый формат, выше в будущем уйдет
    metricClient.sendEvent(
      {
        category: isPopup ? 'site-pop-up' : 'site-body',
        entity: formName,
        action: 'Form-submit',
      },
      {
        // начинается с 4 параметра
        param1: 'placeholder',
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: 'site',
        param5: param5StringResult(window.location.pathname),
        param6: isPopup ? 'pop-up' : 'body',
      },
    );
  };

  // Form-submit, удалить, когда переведем на новый формат
  const funnelFormSubmit = () => {
    const dataLayer = {
      event: 'test_event',
      event_category: 'consultation LeadForm', //FIXME: no hardcode
      event_action: 'formSubmit',
      event_label: 'Funnel-form-submit',
      retain_url: checkGetParamsFromUrl(window.location.href),
    };

    const ymData = {
      'Funnel-form-submit': checkGetParamsFromUrl(window.location.href),
    };

    sendAnalytics(dataLayer, ymData, 'Funnel-form-submit');
  };

  const videoPlayAnalytics = (videoSrc: string) => {
    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'Video-play',
      event_label: 'Video-play',
      retain_url: checkGetParamsFromUrl(window.location.href),
      video_id: videoSrc,
    };

    const ymData = {
      'Video-play': {
        [checkGetParamsFromUrl(window.location.href)]: videoSrc,
      },
    };

    sendAnalytics(dataLayerToSend, ymData, 'Video-play');
  };

  const contactInteractionAnalytics = ({
    interactionType,
    contact,
    interactionText,
  }: ContactInteractionAnalytics) => {
    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_category: 'forms',
      event_action: 'Email-interaction',
      event_label: 'Contact-interaction',
      retain_url: checkGetParamsFromUrl(window.location.href),
      interactionType: interactionType,
      contact: contact,
      interactionText: interactionText,
    };

    const ymData = {
      'Contact-interaction': {
        'Email-interaction': {
          [checkGetParamsFromUrl(window.location.href)]: {
            [interactionType]: {
              [contact]: interactionText,
            },
          },
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, 'Contact-interaction');
  };

  const customAnalytics = (reachGoal: string, params: string[]) => {
    const dataLayerToSend: { [key: string]: string } = {};

    const generateNestedObject = (params: string[]) => {
      if (params.length < 2) {
        return {};
      }

      const lastParam = params.pop();
      return params.reverse().reduce((acc: any, param: string) => {
        return { [param]: acc };
      }, lastParam);
    };

    params.unshift(reachGoal);

    const ymData = generateNestedObject(params);

    sendAnalytics(dataLayerToSend, ymData, reachGoal);
  };

  const searchClickAnalytics = ({
    link,
    searchPhrase,
    type,
    source,
  }: SearchClicksAnalyticsType) => {
    const analyticsType = 'Search-click';

    const dataLayerToSend: { [key: string]: string } = {
      event: 'test_event',
      event_action: analyticsType,
      event_label: analyticsType,
      retain_url: checkGetParamsFromUrl(window.location.href),
      search_value: searchPhrase,
      search_type: type,
    };

    const currentLink = detectExternalLink(link)
      ? link
      : `${window.location.host}${link}`;

    const ymData = {
      [analyticsType]: {
        [checkGetParamsFromUrl(window.location.href)]: {
          [currentLink]: {
            [searchPhrase]: {
              [type]: source || '',
            },
          },
        },
      },
    };

    sendAnalytics(dataLayerToSend, ymData, analyticsType);
  };

  const searchResultClickAnalytics = ({
    link,
    searchPhrase,
    type,
    source,
    category,
    requestId,
    positionNumber,
  }: SearchResultClicksAnalytics) => {
    metricClient.sendEvent(
      {
        category: category,
        entity: type,
        action: 'Search-result-click',
      },
      {
        // начинается с 4 параметра
        param1: source || 'all',
        param2: window.location.origin + window.location.pathname,
        param3: checkSearchParamsFromUrl(window.location.href),
        param4: searchPhrase,
        param5: requestId,
        param6: link,
        param7: positionNumber,
      },
    );
  };

  // Event №10 Form-submit
  const successfullSubmissionFormAnalytics = ({
    zoneclick,
    formname,
  }: SuccessfullSubmissionFormAnalyticsType) => {
    const pathname =
      checkGetParamsFromUrl(window.location.pathname)
        .replace('/', '')
        .replaceAll('/', '-') || 'index';

    const title = `FS_${zoneclick}_${formname}_${pathname}`;

    const dataLayerToSend: { [key: string]: string } = {
      event: title,
    };

    const ymData = { [title]: '' };

    sendAnalytics(dataLayerToSend, ymData, title);
  };

  // Form-field-fill добавить

  return {
    allFormSent,
    funnelFormSubmit,
    funnelFormInput,
    sendAnalytics,
    fillFormField,
    searchAnalytics,
    searchClickAnalytics,
    clickAnalytics,
    tryFormAnalytics,
    contactInteractionAnalytics,
    videoPlayAnalytics,
    serpClickAnalytics,
    customAnalytics,
    pageViewCustom,
    successfullSubmissionFormAnalytics,
    formInteractionSent,
    formViewSent,
    searchAnalyticsWithRequestId,
    searchResultClickAnalytics,
  };
};
